import {
  useAsync,
  useDesign,
  useServices
} from '@app/hooks';
import styles from '@app/styles/components/modal.module.scss';
import {
  AudioType,
  DesignLanguage
} from '@app/types';
import {
  Box,
  Button,
  Icon,
  NewModal,
  Table
} from '@platform/ui-library';
import { ModalProps } from '@platform/ui-library/build/NewModal/types';
import { dataType } from '@platform/ui-library/build/Table/types';
import { FC, useMemo } from 'react';
import { LanguageForm, languageTypeLabels } from './language-form';

type LanguageListProps = (
  Pick<ModalProps, 'isOpen'>
  & {
    onChange: () => void;
    onComplete: () => void;
  }
);

export const LanguageList: FC<LanguageListProps> = ({
  onChange,
  onComplete,
  ...modalProps
}) => {
  const serialized = useDesign();
  const { languages } = serialized;
  const { workflowService } = useServices();

  const [languageOptions] = useAsync(() => workflowService.getLanguages(), []);
  const [allVoices] = useAsync(() => workflowService.getVoices(), []);

  const handleSave = (language: DesignLanguage, action: 'update' | 'delete', index = -1) => {
    let languages = [
      ...serialized.languages
    ];
    if (action === 'delete') {
      languages = languages.filter((_, i) => i !== index);
    } else {
      languages[(index === -1) ? languages.length : index] = language;
    }

    window.apm.setBasic('languages', languages);
    serialized.languagesMeta = serialized.languagesMeta ?? {} as any;
    serialized.languagesMeta!.lastLocalChange = window.apm.timestamp;

    window.apm.setSerializedChanged();
    window.apm.postEventToAllViews({ target: 'request-refresh' });

    onChange();
  };

  const tableData: dataType = useMemo(() => {
    return languages.map((language, index) => {
      const { language: languageCode, ttsFallBackVoice, voice } = language;
      const languageObject = languageOptions?.find(({ value }) => value === languageCode);
      const languageText = languageObject?.label ?? languageCode;
      const [languageName, languageDescription] = splitParentheses(languageText);
      const ttpObject = allVoices?.find(({ value }) => value === voice);
      const ttpName = ttpObject?.label ?? voice;
      const ttpVendor = (ttpName && ttpObject?.vendor) ?? null;
      const ttsObject = allVoices?.find(({ value }) => value === ttsFallBackVoice);
      const ttsName = ttsObject?.label ?? ttsFallBackVoice;
      const ttsDescription = (ttsName && ttsObject?.description) ?? null;
      const ttsVendor = (ttsName && ttsObject?.vendor) ?? null;
      return {
        [languageTypeLabels.language]: {
          content: (
            <>
              <strong>{languageName}</strong>
              {!!languageDescription && <small> ({languageDescription})</small>}
            </>
          ),
          raw: languageName
        },
        [languageTypeLabels[AudioType.ttp]]: {
          content: (
            <>
              {ttpName} {ttpVendor && <small> ({ttpVendor})</small>}
            </>
          )
        },
        [languageTypeLabels[AudioType.tts]]: {
          content: (
            <>
              {ttsName} {ttsVendor && <small> ({ttsVendor})</small>}
              {ttsDescription && <p className={styles.description}>{ttsDescription}</p>}
            </>
          ),
          raw: ttsName
        },
        _action: (
          <LanguageForm
            deletable={index !== 0}
            labels={{
              [AudioType.tts]: ttsName ?? '',
              [AudioType.ttp]: ttpName ?? '',
              'language': languageText ?? ''
            }}
            language={language}
            onSave={(updatedLanguage: DesignLanguage, action) => handleSave(updatedLanguage, action, index)}
          >
            <Icon icon="edit" />
          </LanguageForm>
        )
      };
    });
  }, [languages, languageOptions, allVoices]);

  return (
    <NewModal {...modalProps}>
      <Box className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h3>{window.l.popup.languages}</h3>
            <LanguageForm
              deletable={false}
              onSave={(updatedLanguage: DesignLanguage) => handleSave(updatedLanguage, 'update')}
            >
              <Icon icon="plus" />
              <span>Add Language</span>
            </LanguageForm>
          </div>
          <div className={styles.tableWrapper}>
            <Table data={tableData} key={JSON.stringify(languages)} />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            onClick={() => {
              onComplete();
            }}
            theme="outline"
            type='button'
          >
            Close
          </Button>
        </div>
      </Box>
    </NewModal>
  );
};

const splitParentheses = (text: string): [string, string] => {
  const match = text.match(/(.*?)\s*\((.*?)\)/);
  if (!match) {
    return [text, ''];
  }
  return [match[1].trim(), match[2].trim()];
};
